import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { OrderUnitOfMeasure } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { memo } from 'react';
import { Control, useWatch } from 'react-hook-form';

import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { InvoiceLineItem } from '~hooks/useInvoiceLineItems/models';
import { Invoice } from '~hooks/useInvoices/models';
import { dateFormat } from '~utils/dateTime';
import { toDecimal } from '~utils/decimal';
import { getUnambiguousDeliveredUnitOfMeasure } from '~utils/jobs/job-utils';
import { numberToIntegerOrDecimal } from '~utils/utilFunctions';

import { EM_DASH } from '../SettlementDetails/constants';
import {
  formatAccountingAmount,
  getLineItemRateTypeLabel,
  getLineItemTypeLabel,
  SettlementSheetAmountInput,
  SettlementSheetCell,
  SettlementSheetRemoveButton,
  settlementSheetRowSx,
  SHEET_DATE_FORMAT,
} from './settlementSheetDisplay';
import { SettlementSheetFormValues } from './settlementSheetForm';
import { SettlementSheetCharge } from './settlementSheetLines';

const LOADS_PER_LOAD_LINE = 1;

const formatDeliveredQuantity = (invoice: Invoice, lineItem: InvoiceLineItem) => {
  const unitOfMeasure = getUnambiguousDeliveredUnitOfMeasure(
    invoice.jobSummary,
    lineItem.material?.name ?? null,
  );
  const deliveredQuantity = lineItem.deliveredQuantity ?? 0;
  const hasDeliveredQuantity = deliveredQuantity > 0;
  const isDeliveredInLoads = unitOfMeasure === OrderUnitOfMeasure.LOAD;
  const showsOnlyLoadCount =
    !unitOfMeasure || !hasDeliveredQuantity || isDeliveredInLoads;

  if (showsOnlyLoadCount) {
    return t('approvals.driver_pay.settlements.sheet.loads_count', {
      count: LOADS_PER_LOAD_LINE,
    });
  }

  return t('approvals.driver_pay.settlements.sheet.delivered_quantity', {
    count: LOADS_PER_LOAD_LINE,
    quantity: `${numberToIntegerOrDecimal(deliveredQuantity)} ${unitOfMeasure}`,
  });
};

interface SettlementSheetLineItemRowProps {
  charge: SettlementSheetCharge;
  control: Control<SettlementSheetFormValues>;
  isEditing: boolean;
  isFirstOfCharge: boolean;
  lineItem: InvoiceLineItem;
  onRemoveCharge: ((charge: SettlementSheetCharge) => void) | null;
  rolledUpLineItems: InvoiceLineItem[];
  totalAmount: number | null;
}

function getRolledUpRate(
  lineItem: InvoiceLineItem,
  rolledUpLineItems: InvoiceLineItem[],
) {
  if (rolledUpLineItems.length === 0) {
    return lineItem.rate;
  }

  const lineItems = [lineItem, ...rolledUpLineItems];
  const sharesRateTypeAndQuantity = lineItems.every(
    ({ quantity, rateType }) =>
      rateType === lineItem.rateType && quantity === lineItem.quantity,
  );

  if (!sharesRateTypeAndQuantity || lineItems.some(({ rate }) => isNil(rate))) {
    return null;
  }

  return lineItems
    .reduce((sum, { rate }) => sum.plus(toDecimal(rate)), toDecimal(0))
    .toNumber();
}

function SettlementSheetLineItemRow({
  charge,
  control,
  isEditing,
  isFirstOfCharge,
  lineItem,
  onRemoveCharge,
  rolledUpLineItems,
  totalAmount,
}: SettlementSheetLineItemRowProps) {
  const { currencyFormatter } = useCompanyCurrency();
  const draft = useWatch({ control, name: `lineItems.${lineItem.id}` });
  const isEditable = isEditing && draft !== undefined;
  const { invoice } = charge;
  const { load } = lineItem;
  const isRolledUp = rolledUpLineItems.length > 0;
  const shownRate = getRolledUpRate(lineItem, rolledUpLineItems);

  return (
    <Box data-test-id="settlement-sheet-line-item" sx={settlementSheetRowSx}>
      {isFirstOfCharge && (
        <>
          <Box sx={{ gridColumn: 'date' }}>
            <Typography variant="body2">
              {invoice.job ? dateFormat(invoice.job.jobStartAt, SHEET_DATE_FORMAT) : ''}
            </Typography>
            {load?.loadId && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {load.loadId}
              </Typography>
            )}
          </Box>
          <SettlementSheetCell column="truck">
            {invoice.equipmentExternalIds.join(', ')}
          </SettlementSheetCell>
          <SettlementSheetCell column="material">
            {lineItem.material?.name ?? invoice.materialNames.join(', ')}
          </SettlementSheetCell>
          <SettlementSheetCell column="ticket">
            {load?.ticket?.ticketNumber}
          </SettlementSheetCell>
          <SettlementSheetCell column="deliveredQuantity">
            {load && formatDeliveredQuantity(invoice, lineItem)}
          </SettlementSheetCell>
        </>
      )}
      <SettlementSheetCell column="type">
        {isRolledUp
          ? t('approvals.driver_pay.settlements.sheet.freight_and_material')
          : getLineItemTypeLabel(lineItem)}
      </SettlementSheetCell>
      <SettlementSheetCell column="rateType">
        {getLineItemRateTypeLabel(lineItem)}
      </SettlementSheetCell>
      {isEditable ? (
        <SettlementSheetAmountInput
          column="billingQuantity"
          control={control}
          label={t('approvals.driver_pay.settlements.pdf.billing_qty')}
          name={`lineItems.${lineItem.id}.quantity`}
        />
      ) : (
        <SettlementSheetCell column="billingQuantity">
          {numberToIntegerOrDecimal(lineItem.quantity)}
        </SettlementSheetCell>
      )}
      {isEditable && !isNil(lineItem.rate) ? (
        <SettlementSheetAmountInput
          adornment="currency"
          column="rate"
          control={control}
          label={t('approvals.driver_pay.tabs.driver_pay.rate')}
          name={`lineItems.${lineItem.id}.rate`}
        />
      ) : (
        <SettlementSheetCell column="rate">
          {isNil(shownRate)
            ? EM_DASH
            : formatAccountingAmount(shownRate, currencyFormatter)}
        </SettlementSheetCell>
      )}
      <SettlementSheetCell column="total" isRightAligned>
        {totalAmount === null
          ? EM_DASH
          : formatAccountingAmount(totalAmount, currencyFormatter)}
      </SettlementSheetCell>
      {onRemoveCharge && (
        <SettlementSheetRemoveButton
          label={t('approvals.driver_pay.settlements.lines.remove')}
          onClick={() => onRemoveCharge(charge)}
        />
      )}
    </Box>
  );
}

export default memo(SettlementSheetLineItemRow);
